import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  UserRound,
  HeadphonesIcon,
  LayoutDashboard,
  Building,
  Settings,
  BarChart3,
  Bot,
  ClipboardCheck,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  HelpCircle,
  MoreHorizontal,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useActiveProject } from "@/hooks/useActiveProject";

import { supabase } from '@/lib/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery } from '@tanstack/react-query';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  collapsed?: boolean;
  toggleCollapsed?: () => void;
}

export function Sidebar({ isOpen, toggleSidebar, collapsed = false, toggleCollapsed }: SidebarProps) {
  const location = useLocation();
  const { projects, setProjects, activeProjectId, setActiveProjectId, activeProject } = useActiveProject();
  const { user } = useAuth();
  const { role, loading: loadingRole } = useUserRole();
  const [isMobile, setIsMobile] = useState(false);

  useQuery({
    queryKey: ['projects', role, user?.id],
    queryFn: async () => {
      if (!user) throw new Error('Non authentifié');

      let query = supabase
        .from('projects')
        .select('*, user_projects!inner(*)')
        .eq('user_projects.user_id', user.id);

      if (role === 'superadmin') {
        query = supabase.from('projects').select('*');
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data) {
        setProjects(data);
        if (!activeProjectId || !data.some(p => p.id === activeProjectId)) {
          setActiveProjectId(data.length > 0 ? data[0].id : null);
        }
      }
      return data;
    },
    enabled: !loadingRole && !!user,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 768);
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // §3 du plan de modernisation : navigation principale courte, réglages et
  // aide dans une zone secondaire. « Agents » n'est pas renommé « Équipes &
  // agents » tant que la page n'offre pas de vue par équipe.
  const menuItems = [
    { icon: LayoutDashboard, label: "Vue d'ensemble", shortLabel: "Synthèse", path: "/" },
    { icon: HeadphonesIcon, label: "Appels", shortLabel: "Appels", path: "/appels" },
    { icon: UserRound, label: "Agents", shortLabel: "Agents", path: "/agents" },
    { icon: BarChart3, label: "Audits", shortLabel: "Audits", path: "/rapports-audit" },
    { icon: ClipboardCheck, label: "Procédures", shortLabel: "Procédures", path: "/procedures" },
    { icon: Bot, label: "Copilote", shortLabel: "Copilote", path: "/assistant" },
  ];
  const secondaryItems = [
    { icon: Settings, label: "Réglages", shortLabel: "Réglages", path: "/reglages" },
    { icon: HelpCircle, label: "Aide", shortLabel: "Aide", path: "/aide" },
  ];
  // Sur mobile, peu de destinations immédiates ; les autres sont dans « Plus ».
  const mobilePrimaryPaths = ["/", "/appels", "/rapports-audit"];

  // Une entrée reste active sur ses sous-pages : « Appels » sur la fiche d'un appel.
  const isPathActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleProjectChange = (projectId: number) => setActiveProjectId(projectId);
  const hasMultipleProjects = (projects?.length ?? 0) > 1;
  const sortedProjects = projects ? [...projects].sort((a, b) => a.name.localeCompare(b.name)) : [];

  /* ─────────────  MOBILE BOTTOM BAR  ───────────── */
  if (isMobile) {
    const mobilePrimary = menuItems.filter((item) => mobilePrimaryPaths.includes(item.path));
    const mobileMore = [...menuItems.filter((item) => !mobilePrimaryPaths.includes(item.path)), ...secondaryItems];
    const moreIsActive = mobileMore.some((item) => isPathActive(item.path));

    return (
      <>
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={toggleSidebar}
            aria-hidden="true"
          />
        )}
        <nav
          aria-label="Navigation principale"
          className="fixed z-50 bottom-0 left-0 right-0 h-16 w-full border-t border-border bg-card flex items-center px-2"
        >
          <div className="flex flex-row h-full justify-around items-center w-full">
            {mobilePrimary.map((item) => {
              const isActive = isPathActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex flex-col items-center justify-center h-full flex-1 px-1 transition-colors relative",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium leading-tight">{item.shortLabel}</span>
                  {isActive && (
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b-full bg-primary" />
                  )}
                </Link>
              );
            })}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "flex flex-col items-center justify-center flex-1 h-full px-1 transition-colors",
                    moreIsActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <MoreHorizontal className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium leading-tight">Plus</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="end" className="w-60 mb-3">
                {mobileMore.map((item) => (
                  <DropdownMenuItem key={item.path} asChild className="cursor-pointer">
                    <Link to={item.path} className={cn(isPathActive(item.path) && "text-primary font-semibold")}>
                      <item.icon className="mr-3 h-4 w-4" />
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                {hasMultipleProjects && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                      Projet : {activeProject?.name ?? "—"}
                    </DropdownMenuLabel>
                    {sortedProjects.map((project) => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => handleProjectChange(project.id)}
                        className="cursor-pointer"
                      >
                        <Building className="mr-3 h-4 w-4 text-muted-foreground" />
                        <span className={cn("text-sm", project.id === activeProjectId && "text-primary font-semibold")}>
                          {project.name}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>
      </>
    );
  }

  /* ─────────────  DESKTOP RAIL  ───────────── */
  const renderNavLink = (item: typeof menuItems[number]) => {
    const isActive = isPathActive(item.path);
    const link = (
      <Link
        key={item.path}
        to={item.path}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "relative flex items-center rounded-lg transition-colors duration-150",
          collapsed ? "h-10 w-10 justify-center mx-auto" : "h-10 px-3 gap-3",
          isActive
            ? "bg-primary/10 text-primary font-semibold"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-primary" />
        )}
        <item.icon className="h-[18px] w-[18px] shrink-0" />
        {!collapsed && <span className="text-sm truncate">{item.label}</span>}
      </Link>
    );

    return collapsed ? (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={12} className="text-xs font-medium">
          {item.label}
        </TooltipContent>
      </Tooltip>
    ) : link;
  };

  const projectName = activeProject?.name ?? "Aucun projet";
  const projectInitial = projectName.charAt(0).toUpperCase();
  const projectBadge = (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-foreground">
      {projectInitial}
    </span>
  );
  // Le projet actif est identifiable près du haut de la navigation, même
  // quand l'utilisateur n'en a qu'un.
  const projectTrigger = collapsed ? (
    <Button
      variant="ghost"
      className="h-10 w-10 mx-auto p-0"
      aria-label={`Projet actif : ${projectName}${hasMultipleProjects ? " — changer de projet" : ""}`}
    >
      {projectBadge}
    </Button>
  ) : (
    <Button
      variant="outline"
      className="h-auto w-full justify-start gap-2.5 px-2 py-1.5 text-left"
      aria-label={`Projet actif : ${projectName}${hasMultipleProjects ? " — changer de projet" : ""}`}
    >
      {projectBadge}
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-muted-foreground leading-tight">Projet</span>
        <span className="block truncate text-sm font-semibold text-foreground leading-tight">{projectName}</span>
      </span>
      {hasMultipleProjects && <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />}
    </Button>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={cn(
          "fixed z-50 top-0 left-0 h-screen border-r border-border bg-card",
          "transition-[width] duration-200 ease-out",
          collapsed ? "w-[64px]" : "w-[220px]",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full py-3 px-2">
          {/* Marque */}
          <div className={cn(
            "flex items-center gap-2.5 px-2 mb-3 h-10",
            collapsed && "justify-center px-0"
          )}>
            <div className="w-8 h-8 shrink-0 rounded-lg bg-primary flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
            {!collapsed && (
              <span className="text-base font-semibold tracking-tight font-editorial truncate">
                CallCenter
              </span>
            )}
          </div>

          {/* Projet actif */}
          <div className="mb-3">
            {hasMultipleProjects ? (
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>{projectTrigger}</DropdownMenuTrigger>
                  </TooltipTrigger>
                  {collapsed && (
                    <TooltipContent side="right" sideOffset={12} className="text-xs font-medium">
                      Projet : {projectName}
                    </TooltipContent>
                  )}
                </Tooltip>
                <DropdownMenuContent side="right" align="start" className="w-60 ml-2">
                  <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                    Changer de projet
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {sortedProjects.map((project) => (
                    <DropdownMenuItem
                      key={project.id}
                      onClick={() => handleProjectChange(project.id)}
                      className="cursor-pointer"
                    >
                      <Building className="mr-3 h-4 w-4 text-muted-foreground" />
                      <span className={cn("text-sm", project.id === activeProjectId && "text-primary font-semibold")}>
                        {project.name}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-center" tabIndex={0} aria-label={`Projet actif : ${projectName}`}>{projectBadge}</div>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12} className="text-xs font-medium">
                  Projet : {projectName}
                </TooltipContent>
              </Tooltip>
            ) : (
              <div className="flex items-center gap-2.5 rounded-lg border border-border px-2 py-1.5">
                {projectBadge}
                <span className="min-w-0">
                  <span className="block text-xs text-muted-foreground leading-tight">Projet</span>
                  <span className="block truncate text-sm font-semibold leading-tight">{projectName}</span>
                </span>
              </div>
            )}
          </div>

          {/* Navigation principale */}
          <nav aria-label="Navigation principale" className="flex flex-col gap-0.5 flex-1">
            {menuItems.map(renderNavLink)}
          </nav>

          {/* Zone secondaire : réglages, aide, repli */}
          <div className="mt-auto pt-2 border-t border-border flex flex-col gap-0.5">
            <nav aria-label="Navigation secondaire" className="flex flex-col gap-0.5">
              {secondaryItems.map(renderNavLink)}
            </nav>

            {toggleCollapsed && (
              collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      onClick={toggleCollapsed}
                      className="h-9 w-10 mx-auto p-0 text-muted-foreground"
                      aria-label="Étendre la navigation"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={12} className="text-xs font-medium">
                    Étendre
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  onClick={toggleCollapsed}
                  className="h-9 px-3 justify-start gap-3 text-muted-foreground"
                  aria-label="Réduire la navigation"
                >
                  <ChevronLeft className="h-4 w-4 shrink-0" />
                  <span className="text-sm">Réduire</span>
                </Button>
              )
            )}
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}

export default Sidebar;
